import { useCallback, useEffect, useMemo, useState } from "react";

import { useHistory, useParams } from "react-router-dom";
import { CommitmentManagersModel } from "@doitintl/cmp-models";
import { getCollection, type ModelReference } from "@doitintl/models-any";
import { Alert, Card, Stack } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";

import { getCurrencyByCode } from "../../utils/common";
import { CommitmentChart } from "./CommitmentChart";
import { CommitmentDataTable } from "./CommitmentDataTable";
import { CommitmentManagerHeader } from "./CommitmentManagerHeader";
import { CommitmentMetadataCards } from "./CommitmentMetadataCards";
import { calcTableData, getCurrentPeriodIndex } from "./utils";

export const CommitmentManager = () => {
  const { commitmentId } = useParams<{ commitmentId: string }>();
  const history = useHistory();
  const [commitmentManager, setCommitmentManager] = useState<CommitmentManagersModel | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [docRef, setDocRef] = useState<ModelReference<CommitmentManagersModel> | undefined>(undefined);
  const [periodIndex, setPeriodIndex] = useState<number | undefined>(undefined);
  const currencyCode = getCurrencyByCode(commitmentManager?.currency ?? "USD");

  const tableData = useMemo(() => calcTableData(commitmentManager), [commitmentManager]);

  const marketplaceLimitAmount = useCallback(
    (periodIndex: number) => {
      if (!commitmentManager) {
        return 0;
      }

      const period = commitmentManager.periods[periodIndex];
      return (Number(period.marketplaceLimitPercentage) / 100) * period.commitmentValue;
    },
    [commitmentManager]
  );

  useEffect(() => {
    if (!commitmentId) {
      return;
    }

    return getCollection(CommitmentManagersModel)
      .doc(commitmentId)
      .onSnapshot((snap) => {
        setDocRef(snap.ref);
        const data = snap.asModelData();

        if (!data) {
          history.push(history.location.pathname.split("/").slice(0, -1).join("/"));
          return;
        }

        setCommitmentManager(data);
        setLoading(false);

        if (data.periods?.length && periodIndex === undefined) {
          setPeriodIndex(getCurrentPeriodIndex(data.periods));
        }
      });
  }, [commitmentId, history, periodIndex]);

  const isDataMissing =
    !commitmentManager ||
    !docRef ||
    periodIndex === undefined ||
    !commitmentManager.periodsSpend?.[periodIndex]?.MonthlySpend ||
    !tableData[periodIndex] ||
    !commitmentManager.periodsSpend?.[periodIndex]?.commitmentSeries ||
    !commitmentManager.periodsSpend?.[periodIndex]?.marketplaceSeries ||
    !commitmentManager.periodsSpend?.[periodIndex]?.totalSeries;

  if (isDataMissing || loading) {
    return (
      <>
        {loading ? (
          <CircularProgress sx={{ position: "absolute", left: "50%", top: "50%" }} />
        ) : (
          <Stack spacing={2} sx={{ p: 2 }}>
            <Alert severity="error" data-cy="commitment-manager-missing-data-info">
              Some data is missing and the commitment cannot be displayed. Please contact support.
            </Alert>
          </Stack>
        )}
      </>
    );
  }

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <CommitmentManagerHeader
        name={commitmentManager.name}
        docRef={docRef}
        periods={commitmentManager.periods}
        selectedPeriod={periodIndex ?? 0}
        onPeriodChange={setPeriodIndex}
      />
      <Card>
        <CommitmentChart
          key={`chart-${periodIndex}`}
          commitmentSeries={commitmentManager.periodsSpend![periodIndex].commitmentSeries}
          marketplaceSeries={commitmentManager.periodsSpend![periodIndex].marketplaceSeries}
          totalSeries={commitmentManager.periodsSpend![periodIndex].totalSeries}
          forecastSeries={commitmentManager.periodsSpend![periodIndex].forecastSeries}
          currencyCode={currencyCode}
          marketplaceLimitAmount={marketplaceLimitAmount(periodIndex)}
        />
      </Card>
      <CommitmentMetadataCards
        commitmentManagerRef={docRef}
        commitmentManager={commitmentManager}
        periodIndex={periodIndex}
        currencyCode={currencyCode}
      />
      <CommitmentDataTable
        key={`table-${periodIndex}`}
        tableData={tableData[periodIndex]}
        monthlySpend={commitmentManager.periodsSpend![periodIndex].MonthlySpend}
        currencyCode={currencyCode}
        marketplaceLimitAmount={marketplaceLimitAmount(periodIndex)}
      />
    </Stack>
  );
};
